<template>
  <v-dialog :value="sectionEditor.show" :width="1000" @input="handleClosePopup" :retain-focus="false">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div>
          <label class="label-input">Section Name</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox small v-model="fields.enableAnchor" label="Enable Anchor" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mt-6">
          <label class="label-input">Title</label>
          <ckeditor :editor="editor" v-model="fields.content" :config="editorConfig" />
        </div>
        <div class="mt-6">
          <div>
            <div class="d-flex justify-space-between align-center mb-2">
              <label class="label-input mb-2">Items</label>
              <v-btn small color="primary" @click="handleAddItem()">
                <v-icon size="16">mdi-plus</v-icon>
                <span>Add Item</span>
              </v-btn>
            </div>
            <div class="group-item">
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    type="number"
                    :rules="[
                      v => Number(v) > 0 || 'Items Per Row must be greater than 0',
                      v => Number(v) <= maxItemsPerRow || `Items Per Row must be <= ${maxItemsPerRow}`,
                    ]"
                    min="1"
                    v-model="fields.itemsPerRow.mobile"
                    label="Items Per Row (Mobile)"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    type="number"
                    :rules="[
                      v => Number(v) > 0 || 'Items Per Row must be greater than 0',
                      v => Number(v) <= maxItemsPerRow || `Items Per Row must be <= ${maxItemsPerRow}`,
                    ]"
                    min="1"
                    v-model="fields.itemsPerRow.desktop"
                    label="Items Per Row (Desktop)"
                  />
                </v-col>
                <v-col cols="12" md="4">
                  <v-text-field
                    type="number"
                    v-model.number="fields.itemImageHeight"
                    label="Item Image Height"
                    min="0"
                    suffix="px"
                    :rules="[v => v >= 0 || 'Item Image Height must be greater than or equal 0']"
                  />
                </v-col>
              </v-row>
              <v-expansion-panels accordion>
                <v-expansion-panel v-for="(item, i) in fields['items']" :key="i">
                  <v-expansion-panel-header>
                    <div class="d-flex justify-space-between align-center">
                      <div class="d-flex align-center">
                        <div class="line-clamp-1 ml-2" style="flex: 1">{{ item.title }}</div>
                      </div>
                      <v-btn icon @click.stop="handleRemoveItem(i)">
                        <v-icon size="20" color="#ff6c6c">mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div>
                      <v-text-field v-model="item.title" label="Title" />
                    </div>
                    <div>
                      <p class="mb-2">Content</p>
                      <v-textarea outlined v-model="item.content" placeholder="Content" />
                    </div>
                    <div>
                      <p>Image</p>
                      <UploadImage
                        @callback="obj => handleUploadImage(obj, i)"
                        :image="{ imageSrc: item.image }"
                        :height="`150`"
                        :typeUpload="'image'"
                        @callbackDelete="handleRemoveImage(i)"
                      />
                    </div>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </div>
        </div>

        <div class="mt-6">
          <label class="label-input">Section Settings</label>
          <div class="group-item">
            <div>
              <div class="font-weight-medium">Spacing</div>
              <v-row>
                <v-col cols="6">
                  <v-checkbox v-model="fields.topPadding" label="Top Padding" dense hide-details />
                </v-col>
                <v-col cols="6">
                  <v-checkbox v-model="fields.bottomPadding" label="Bottom Padding" dense hide-details />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3 mb-2">Container Colors</div>
              <v-row>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields.colors.backgroundColor"
                    name="backgroundColor"
                    label="Background Color"
                    default-color="#ffffff"
                  />
                </v-col>
                <v-col md="6" cols="12">
                  <ColorPicker
                    v-model="fields.colors.textColor"
                    name="textColor"
                    label="Text Color"
                    default-color="#000000"
                  />
                </v-col>
              </v-row>
            </div>
            <div class="mt-6">
              <div class="font-weight-medium mb-3 mb-2">Item Colors</div>
              <v-row>
                <v-col md="3" cols="12">
                  <ColorPicker
                    v-model="fields.colors.oddBackgroundColor"
                    name="oddBackgroundColor"
                    label="Odd Background Color"
                    default-color="#14a16c"
                  />
                </v-col>
                <v-col md="3" cols="12">
                  <ColorPicker
                    v-model="fields.colors.oddTextColor"
                    name="oddTextColor"
                    label="Odd Text Color"
                    default-color="#ffffff"
                  />
                </v-col>

                <v-col md="3" cols="12">
                  <ColorPicker
                    v-model="fields.colors.evenBackgroundColor"
                    name="evenBackgroundColor"
                    label="Even Background Color"
                    default-color="#e1e1e1"
                  />
                </v-col>
                <v-col md="3" cols="12">
                  <ColorPicker
                    v-model="fields.colors.evenTextColor"
                    name="evenTextColor"
                    label="Even Text Color"
                    default-color="#000000"
                  />
                </v-col>
              </v-row>
            </div>
          </div>
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer></v-spacer>
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { updateHtmlFromData } from '../common/html/update';
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import ColorPicker from './common/color-picker.vue';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import { replaceImageUrl } from '../common/utils';

export default {
  name: 'card-step-instructions-02-section-editor',
  components: {
    UploadImage,
    ColorPicker,
  },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: 'Card Step Instructions 02 Section',
        enableAnchor: false,
        colors: {
          backgroundColor: '#ffffff',
          textColor: '#000000',
          oddBackgroundColor: '#14a16c',
          oddTextColor: '#ffffff',
          evenBackgroundColor: '#e1e1e1',
          evenTextColor: '#000000',
        },
        header: '',
        topPadding: true,
        bottomPadding: true,
        items: [
          {
            title: '',
            content: '',
            image: '',
          },
        ],
        itemsPerRow: {
          mobile: 1,
          desktop: 4,
        },
        itemImageHeight: 150,
      },
    };
  },
  computed: {
    maxItemsPerRow() {
      return Math.max(1, this.fields.items?.length || 0);
    },
  },
  mounted() {
    if (this.sectionEditor.sectionExtract) {
      this.fields = {
        ...this.fields,
        ...this.sectionEditor.sectionExtract,
      };
    }
  },
  watch: {
    sectionEditor: {
      handler(newVal) {
        if (newVal.sectionExtract) {
          this.fields = {
            ...this.fields,
            ...newVal.sectionExtract,
          };
        }
      },
      deep: true,
    },
  },
  methods: {
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const updateHtml = updateHtmlFromData.sectionCardStepInstructions02(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html: updateHtml,
      });
      this.$emit('close');
    },
    handleUploadImage(obj, index) {
      this.fields.items[index].image = obj.imageSrc;
    },
    handleRemoveImage(index) {
      this.fields.items[index].image = '';
    },
    handleAddItem() {
      this.fields.items.push({
        title: 'Title text',
        content: 'Content text',
        image: replaceImageUrl(
          'https://cdn.skyfastimg.com/images/2026/04/11/image-69d9ed45411616b8d572ad7f.png?w=500&h=50&q=80',
        ),
      });
      this.normalizeItemsPerRow();
    },
    handleRemoveItem(index) {
      if (index) {
        this.fields.items.splice(index, 1);
        this.normalizeItemsPerRow();
      }
    },
    normalizeItemsPerRow() {
      const max = this.maxItemsPerRow;
      const mobile = Number(this.fields.itemsPerRow?.mobile);
      const desktop = Number(this.fields.itemsPerRow?.desktop);

      if (!this.fields.itemsPerRow) {
        this.$set(this.fields, 'itemsPerRow', { mobile: max, desktop: max });
        return;
      }

      const safeMobile = Number.isNaN(mobile) || mobile < 1 ? 1 : mobile;
      const safeDesktop = Number.isNaN(desktop) || desktop < 1 ? 1 : desktop;

      this.fields.itemsPerRow.mobile = Math.min(safeMobile, max);
      this.fields.itemsPerRow.desktop = Math.min(safeDesktop, max);
    },
  },
};
</script>

<style scoped lang="scss">
.ck-editor__editable {
  min-height: 100px;
}
.thumbnail-text {
  flex: 1;
  p {
    margin: 0 !important;
  }
}
.item-thumbnail {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
</style>
